import { useCallback, useEffect, useMemo, useState } from "react";

import MatchCard from "../components/MatchCard";
import { api } from "../lib/api";
import type { Match, StreamEvent, Watch } from "../lib/types";

/** The match feed. Live over SSE, backfilled over REST. */
export default function Feed({ event }: { event: StreamEvent | null }) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [watchId, setWatchId] = useState("");
  // New items only by default: the feed answers "what should I look at now", and something
  // you have already acked is, by definition, not that.
  const [unacked, setUnacked] = useState(true);
  const [showBelow, setShowBelow] = useState(false);
  const [total, setTotal] = useState(0);
  const [fresh, setFresh] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.matches({
        watch_id: watchId || undefined,
        unacked: unacked || undefined,
        include_below_threshold: showBelow || undefined,
        limit: 100,
      });
      setMatches(rows.items);
      setTotal(rows.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [watchId, unacked, showBelow]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.watches().then(setWatches).catch(() => undefined);
  }, []);

  // A live match arrives with only the alert payload; refetch to get the stored row.
  useEffect(() => {
    if (!event || event.type !== "match") return;
    setFresh((prev) => new Set(prev).add(event.verdict_id));
    void load();
  }, [event, load]);

  const ack = async (verdictId: number, acked: boolean) => {
    const updated = await api.ackMatch(verdictId, acked);
    setMatches((prev) => prev.map((m) => (m.verdict_id === verdictId ? updated : m)));
  };

  const visible = useMemo(
    () => (unacked ? matches.filter((m) => !m.acked_at) : matches),
    [matches, unacked],
  );

  return (
    <div className="stack">
      <div className="row">
        <select
          value={watchId}
          onChange={(e) => setWatchId(e.target.value)}
          style={{ width: "auto", minWidth: 200 }}
        >
          <option value="">All watches</option>
          {watches.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        <label className="row" style={{ marginBottom: 0, gap: 6 }}>
          <input
            type="checkbox"
            checked={!unacked}
            style={{ width: "auto" }}
            onChange={(e) => setUnacked(!e.target.checked)}
          />
          Include acked
        </label>
        <label
          className="row"
          style={{ marginBottom: 0, gap: 6 }}
          title="Show matches whose confidence is under their watch's minimum. These never alerted."
        >
          <input
            type="checkbox"
            checked={showBelow}
            style={{ width: "auto" }}
            onChange={(e) => setShowBelow(e.target.checked)}
          />
          Below threshold
        </label>
        <div className="spacer" />
        {total > 0 && <span className="faint">{total} total</span>}
        {visible.length > 0 && unacked && (
          <button
            onClick={async () => {
              if (!confirm(`Mark all ${visible.length} shown as seen?`)) return;
              await api.bulkAck({
                verdict_ids: visible.map((m) => m.verdict_id),
                acked: true,
              });
              void load();
            }}
          >
            Mark all seen
          </button>
        )}
        <button onClick={() => void load()} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {!visible.length && !loading ? (
        <div className="empty">
          {unacked
            ? "Nothing new. Tick “Include acked” to see what you have already dealt with."
            : "Nothing matched yet. If the worker is polling and this stays empty, the reject log is where you find out why."}
        </div>
      ) : (
        visible.map((match) => (
          <MatchCard
            key={match.verdict_id}
            match={match}
            fresh={fresh.has(match.verdict_id)}
            onAck={(id, acked) => void ack(id, acked)}
          />
        ))
      )}
    </div>
  );
}
